import { RUNNIT_NODE_FIELD_TYPE, RunnitNodeFieldType } from '@run-diffusion/shared';

/**
 * Field types DynamicChipField knows how to render in the primary form's chip row.
 *
 * This is the single source of truth for two call sites that must agree:
 *   - DynamicChipField.vue, which picks the chip component per type.
 *   - AutoSaveRunnitNodeRunContainer.initPromptDrivenFormFieldGrouping, which
 *     routes `primaryForm: 'CHIP'` fields into chipFields.
 *
 * When they disagree the field disappears from the form: the grouping pulls it
 * out of advancedFields, then the renderer has no branch for it and emits an
 * empty div. Keep both sides driven off this list.
 */
export const CHIP_RENDERABLE_FIELD_TYPES: RunnitNodeFieldType[] = [
	RUNNIT_NODE_FIELD_TYPE.WIDTH_HEIGHT,
	RUNNIT_NODE_FIELD_TYPE.SINGLE_SELECT,
	RUNNIT_NODE_FIELD_TYPE.SLIDER,
	RUNNIT_NODE_FIELD_TYPE.CFG,
	RUNNIT_NODE_FIELD_TYPE.STEPS,
];

const CHIP_RENDERABLE_FIELD_TYPE_SET = new Set<string>(CHIP_RENDERABLE_FIELD_TYPES);

/**
 * Whether a field of this type can be shown as a chip. A `primaryForm: 'CHIP'`
 * field whose type returns false must fall back to Advanced Settings.
 */
export function hasChipRenderer (type: RunnitNodeFieldType | null | undefined): boolean {
	return !!type && CHIP_RENDERABLE_FIELD_TYPE_SET.has(type);
}

/**
 * Slider types the chip row hosts inside a popover.
 *
 * UPSCALE_FACTOR_SLIDER is deliberately absent. No tool that uses it puts it in
 * the primary form, and supporting it would drag in its max-output-dimension
 * validation just to drive the chip's error state.
 */
export const CHIP_SLIDER_FIELD_TYPES: RunnitNodeFieldType[] = [
	RUNNIT_NODE_FIELD_TYPE.SLIDER,
	RUNNIT_NODE_FIELD_TYPE.CFG,
	RUNNIT_NODE_FIELD_TYPE.STEPS,
];

const CHIP_SLIDER_FIELD_TYPE_SET = new Set<string>(CHIP_SLIDER_FIELD_TYPES);

/** Whether this field type is rendered by the SliderField chip. */
export function isChipSliderFieldType (type: RunnitNodeFieldType | null | undefined): boolean {
	return !!type && CHIP_SLIDER_FIELD_TYPE_SET.has(type);
}
